import { useMemo, useState } from 'react';

import { CaretDownOutlined } from '@ant-design/icons';
import { Checkbox, Dropdown, Empty, Input, List, Segmented, Typography } from 'antd';
import classNames from 'classnames';

import { ClearIcon } from '@/shared/assets/icons/input/ClearIcon';
import { COLOR } from '@/shared/design-tokens/design-tokens';
import { Icon } from '@/shared/ui/Icon';

import styles from './FilterDropdown.module.css';
import type { FilterDropdownOption, FilterDropdownProps } from './types';

const { Text } = Typography;

const defaultFilterOption = <T,>(option: FilterDropdownOption<T>, searchQuery: string): boolean => {
  return option.label.toLowerCase().includes(searchQuery.toLowerCase());
};

export const FilterDropdown = <T extends string | number = string>({
  buttonText,
  searchPlaceholder,
  selectedValues,
  options,
  onSelectionChange,
  renderItem,
  mode = 'multiple',
  getButtonTextForSingleSelection,
  filterOption = defaultFilterOption,
  showOperatorToggle = false,
  operator = 'OR',
  onOperatorChange,
  width,
  showSearch = true,
  disabled = false,
  emptyDescription,
}: FilterDropdownProps<T>) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;
    return options.filter((option) => filterOption(option, searchQuery));
  }, [searchQuery, options, filterOption]);

  const handleToggle = (value: T) => {
    if (mode === 'single') {
      const newValues = selectedValues.includes(value) ? [] : [value];
      onSelectionChange(newValues);
    } else {
      const isCurrentlySelected = selectedValues.includes(value);
      const newValues = isCurrentlySelected ? selectedValues.filter((v) => v !== value) : [...selectedValues, value];
      onSelectionChange(newValues);
    }
  };

  const handleClear = () => {
    onSelectionChange([]);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSearchQuery('');
    }
  };

  const renderButtonValue = () => {
    if (selectedValues.length === 0) {
      return <span className={styles.buttonValueMain}>전체</span>;
    }

    if (selectedValues.length === 1) {
      const selectedOption = options.find((opt) => opt.value === selectedValues[0]);
      const singleLabel =
        selectedOption && getButtonTextForSingleSelection
          ? getButtonTextForSingleSelection(selectedOption)
          : selectedOption?.label;
      return <span className={styles.buttonValueMain}>{singleLabel}</span>;
    }
    const firstOption = options.find((opt) => opt.value === selectedValues[0]);
    const firstLabel = firstOption?.label || selectedValues[0];
    const remainingCount = selectedValues.length - 1;
    return (
      <>
        <span className={styles.buttonValueMain}>{firstLabel}</span>
        <span className={styles.buttonValueSuffix}>, ... +{remainingCount}</span>
      </>
    );
  };

  const dropdownContent = (
    <div
      className={`${styles.dropdownContent} ${!showSearch ? styles.noSearch : ''} ${
        mode === 'single' ? styles.singleMode : ''
      }`}
      style={width ? { width: typeof width === 'number' ? `${width}px` : width } : undefined}>
      {showSearch && (
        <div className={styles.searchSection}>
          <Input
            prefix={<Icon name="search" size={13} />}
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            maxLength={256}
            allowClear={{
              clearIcon: <ClearIcon size={14} backgroundColor={COLOR.GRAY[80]} iconColor={COLOR.GRAY[100]} />,
            }}
            className={styles.searchInput}
          />
          {showOperatorToggle && selectedValues.length > 1 && (
            <div className={styles.operatorSection}>
              <Text type="secondary" style={{ marginRight: 8, fontSize: '12px' }}>
                연산자:
              </Text>
              <Segmented
                options={['OR', 'AND']}
                value={operator}
                onChange={(value) => onOperatorChange?.(value as 'AND' | 'OR')}
                size="small"
              />
            </div>
          )}
        </div>
      )}

      {filteredOptions.length > 0 ? (
        <List
          size="small"
          dataSource={filteredOptions}
          className={`${styles.filterList} ${selectedValues.length > 0 ? styles.hasSelection : ''} ${
            mode === 'single' ? styles.noActions : ''
          }`}
          renderItem={(option) => {
            const isSelected = selectedValues.includes(option.value);
            if (option.key === 'all') {
              return (
                <>
                  <List.Item
                    key={option.key}
                    className={`${styles.listItem} ${option.disabled ? styles.disabled : ''} ${
                      isSelected ? styles.selected : ''
                    }`}
                    onClick={() => !option.disabled && handleToggle(option.value)}>
                    {renderItem(option, isSelected, handleToggle, searchQuery)}
                    {isSelected && mode === 'single' && <Icon name="check" size={16} className={styles.checkIcon} />}
                  </List.Item>
                  <div className={styles.divider} />
                </>
              );
            }
            return (
              <List.Item
                key={option.key}
                className={`${styles.listItem} ${option.disabled ? styles.disabled : ''} ${
                  isSelected ? styles.selected : ''
                }`}
                onClick={() => !option.disabled && handleToggle(option.value)}>
                {mode === 'multiple' && (
                  <Checkbox
                    checked={isSelected}
                    disabled={option.disabled}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggle(option.value);
                    }}
                    style={{ marginRight: 8 }}
                  />
                )}
                {renderItem(option, isSelected, handleToggle, searchQuery)}
                {isSelected && mode === 'single' && <Icon name="check" size={16} className={styles.checkIcon} />}
              </List.Item>
            );
          }}
        />
      ) : (
        <div className={styles.emptyWrapper}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              searchQuery
                ? `"${searchQuery}"에 대한 검색 결과가 없습니다`
                : (emptyDescription ?? '검색 결과가 없습니다')
            }
          />
        </div>
      )}

      {mode === 'multiple' && (
        <div className={styles.actions}>
          <button
            type="button"
            className={`${styles.clearButton} ${selectedValues.length === 0 ? styles.inactive : ''}`}
            onClick={handleClear}>
            선택 해제
          </button>
        </div>
      )}
    </div>
  );

  return (
    <Dropdown
      open={open && !disabled}
      onOpenChange={disabled ? undefined : handleOpenChange}
      popupRender={() => dropdownContent}
      trigger={['click']}
      placement="bottomLeft"
      align={{ offset: [0, 8] }}>
      <button
        type="button"
        className={`${styles.filterButton} ${disabled ? styles.disabled : ''}`}
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}>
        <div className={styles.buttonContent}>
          <span className={styles.buttonLabel}>{buttonText}:</span>
          <span className={styles.buttonValue}>{renderButtonValue()}</span>
        </div>
        <span className={styles.iconWrapper}>
          <CaretDownOutlined className={classNames(styles.dropdownIcon, { [styles.rotated]: open })} />
        </span>
      </button>
    </Dropdown>
  );
};
